export const routes = {
  allocations: {
    // Allocation volumes
    deleteAllocationVolumes: '/allocations/volumes',

    // Allocation routines
    createAllocationRoutine: '/allocations/routines',
    deleteAllocationRoutine: (routineId: string) => `/allocations/routines/${routineId}`,
    getAllocationRoutineDetail: (routineId: string) => `/allocations/routines/${routineId}`,
    getAllocationRoutines: '/allocations/routines',
    setDefaultAllocationRoutine: (routineId: string) => `/allocations/routines/${routineId}/set-default`,
    updateAllocationRoutine: (routineId: string) => `/allocations/routines/${routineId}`,

    // Allocation routine steps
    createAllocationStep: (routineId: string) => `/allocations/routines/${routineId}/steps`,
    deleteAllocationStep: (routineId: string, stepId: string) => `/allocations/routines/${routineId}/steps/${stepId}`,
    reorderAllocationSteps: (routineId: string) => `/allocations/routines/${routineId}/steps/reorder`,
    updateAllocationStep: (routineId: string, stepId: string) => `/allocations/routines/${routineId}/steps/${stepId}`,

    // Allocation finalization requests
    canFinalize: '/allocations/finalization-requests/can-finalize',

    // Allocation history
    getAllocationHistory: '/allocations/history',

    // Allocation batches
    createAllocatedVolumesBatch: '/allocations/batches',
    deleteRegistryInstructions: (batchId: string) => `/allocations/batches/${batchId}/registry-action-instructions`,
    downloadRegistryInstructions: (batchId: string) => `/allocations/batches/${batchId}/registry-action-instructions/download`,
    generateRegistryActionInstructions: (batchId: string) => `/allocations/batches/${batchId}/registry-action-instructions`,
    getAllocatedVolumesBatches: '/allocations/batches',
    getAllocatedVolumesBatchesFilterValues: '/allocations/batches/filter-values',
    getAvailableRegistryTransactionsForAllocatedVolumesBatch: (batchId: string) => `/allocations/batches/${batchId}/available-registry-transactions`,
    getRegistryActionInstructions: (batchId: string) => `/allocations/batches/${batchId}/registry-action-instructions`,
    unlockAllocationBatch: (batchId: string) => `/allocations/batches/${batchId}/unlock`,
    validateRegistryInstructions: (batchId: string) => `/allocations/batches/${batchId}/registry-action-instructions/validate`,
    createBatchesWithRegistryActionInstructions: '/allocations/batches/with-registry-action-instructions',
    createBatchesWithOutboundTransactionReconciliation: '/allocations/batches/with-out-transaction-reconciliation',
    initiateRegistryAction: (batchId: string, registry: string, action: string) => `/allocations/batches/${batchId}/initiate-registry-action/${registry.toLowerCase()}/${action.toLowerCase()}`,
    createBatchWithRegistryAction: (registry: string, action: string) => `/allocations/batches/with-registry-action/${registry.toLowerCase()}/${action.toLowerCase()}`,
    submitRegistryAction: (batchId: string) => `/allocations/batches/${batchId}/registry-action-instructions/submit`,
    getRegistryActionRequests: (batchId: string) => `/allocations/batches/${batchId}/registry-action-instructions/requests`,

    // Allocation runs
    confirmAllocationDraft: (allocationRunId: string) => `/allocations/runs/${allocationRunId}/confirm`,
    createAllocationDraft: '/allocations/runs/draft',
    downloadAllocationRuns: (allocationRunId: string) => `/allocations/runs/${allocationRunId}/download`,
    getAllocationRunDetails: (allocationRunId: string) => `/allocations/runs/${allocationRunId}`,
    getAvailableVolumes: (allocationRunId: string) => `/allocations/runs/${allocationRunId}/available-volumes`,
    removeAllocationDraftConsumer: (allocationRunId: string, consumerId: string) => `/allocations/runs/${allocationRunId}/consumers/${consumerId}`,
    requestAdHocAllocationRun: '/allocations/runs/ad-hoc',
    runAllocationRoutine: '/allocations/runs/routine',
    updateAllocationRun: (allocationRunId: string) => `/allocations/runs/${allocationRunId}`,
    updateConsumptionAllocationDraft: (allocationRunId: string) => `/allocations/runs/${allocationRunId}/consumption-driven/allocate`,
    updateProductionAllocationDraft: (allocationRunId: string, consumerId: string) => `/allocations/runs/${allocationRunId}/allocate/${consumerId}`,
  },
  certificates: {
    getCertificateHoldingBundles: '/certificates/holdings/bundles',
    getCertificateHoldings: '/certificates/holdings',
    getCertificateHoldingTransactions: '/certificates/holdings/transactions',
    getDispatchedIdsAttribution: '/certificates/dispatched-ids-attribution',
    getInventoryAndTransactionsStatus: (id: string) => `/certificates-and-transactions/upload/${id}`,
    uploadInventoryAndTransactions: '/certificates-and-transactions/upload',
  },
  consumers: {
    createConsumer: '/consumers',
    createConsumerMeteringPoint: {
      method: 'POST',
      path: (id: string) => `/consumers/${id}/metering-points`,
    },
    deleteConsumer: (id: string) => `/consumers/${id}`,
    getConsumer: (id: string) => `/consumers/${id}`,
    getConsumerMeteringPointStates: (id: string) => `/consumers/${id}/metering-point-states`,
    getConsumerMeteringPoints: (id: string) => `/consumers/${id}/metering-points`,
    getConsumers: '/consumers',
    updateConsumer: (id: string) => `/consumers/${id}`,
    updateConsumerMeteringPoint: {
      method: 'PATCH',
      path: (consumerId: string, meteringPointId: string) => `/consumers/${consumerId}/metering-points/${meteringPointId}`,
    },
  },
  customViews: {
    createCustomView: '/custom-views',
    getAllCustomViews: '/custom-views',
    getCustomView: (id: string) => `/custom-views/${id}`,
    updateCustomView: (id: string) => `/custom-views/${id}`,
    updateCustomViewsOrder: '/custom-views/sort-order',
  },
  deals: {
    createDeal: '/deals',
    getDealOptions: '/deals',
    getDeals: '/deals',
    getDealsStatus: (id: string) => `/deals/files/${id}`,
    updateDealTranche: (dealId: string, trancheId: string) => `/deals/${dealId}/tranches/${trancheId}`,
    uploadDealsFile: '/deals/files/upload',
  },
  deliveryMonitoring: {
    getDeliveryMonitoringContractedVolumes: '/delivery-monitoring/inbound/contracted-volumes',
    getDeliveryMonitoringDeliveredVolumes: '/delivery-monitoring/inbound/delivered-volumes',
    getDeliveryMonitoringReconciledVolumes: '/delivery-monitoring/inbound/reconciled-volumes',
    getOutboundDeliveryMonitoringContractedVolumes: '/delivery-monitoring/outbound/contracted-volumes',
    getOutboundDeliveryMonitoringDeliveredVolumes: '/delivery-monitoring/outbound/delivered-volumes',
    getOutboundDeliveryMonitoringReconciledVolumes: '/delivery-monitoring/outbound/reconciled-volumes',
  },
  endConsumers: {
    downloadConsumerReport: (id: string) => `/endconsumers/reports/${id}/download`,
    downloadConsumerReports: '/endconsumers/reports/download',
  },
  esna: {
    // Allocations
    downloadAllocations: '/esna/allocations/download',

    // Contracts
    createContract: '/esna/contracts',
    createContractMembership: (contractId: string) => `/esna/contracts/${contractId}/memberships`,
    deleteContract: (contractId: string) => `/esna/contracts/${contractId}`,
    deleteContractMembership: (contractId: string, membershipId: string) => `/esna/contracts/${contractId}/membership/${membershipId}`,
    deleteMembershipPdf: (contractId: string, membershipId: string) => `/esna/contracts/${contractId}/membership/${membershipId}/document`,
    downloadContracts: '/esna/contracts/download',
    downloadMembershipPdf: (contractId: string, membershipId: string) => `/esna/contracts/${contractId}/membership/${membershipId}/download`,
    getContract: (contractId: string) => `/esna/contracts/${contractId}`,
    getContracts: '/esna/contracts',
    getContractsUploadStatus: (id: string) => `/esna/contracts/files/${id}`,
    updateContract: (contractId: string) => `/esna/contracts/${contractId}`,
    updateContractMembership: (contractId: string, membershipId: string) => `/esna/contracts/${contractId}/membership/${membershipId}`,
    uploadContractsFile: '/esna/contracts/files/upload',
    uploadMembershipPdf: (contractId: string, membershipId: string) => `/esna/contracts/${contractId}/membership/${membershipId}/upload`,

    // Expected upload calendar
    downloadExpectedUploadCalendar: '/esna/expected-upload-calendar/download',
    getExpectedUploadCalendar: '/esna/expected-upload-calendar',
    getExpectedUploadCalendarById: (id: string) => `/esna/expected-upload-calendar/${id}`,
    previewInterimRuns: '/esna/expected-upload-calendar/interim-runs/preview',
    triggerInterimRuns: '/esna/expected-upload-calendar/interim-runs',

    // Loss factor codes
    downloadLossFactorCodes: '/esna/loss-factor-codes/download',
    getLossFactorCodes: '/esna/loss-factor-codes',
    getLossFactorCodesUploadStatus: (id: string) => `/esna/loss-factor-codes/upload/files/${id}/status`,
    uploadLossFactorCodesFile: '/esna/loss-factor-codes/upload',
  },
  import: {
    getUploadedConsumptionFileStatus: (id: string) => `/multipoint-consumption-files/${id}`,
    getUploadedProductionFileStatus: (id: string) => `/multipoint-production-files/${id}`,
    uploadConsumptionFile: '/multipoint-consumption-files/upload',
    uploadProductionFile: '/multipoint-production-files/upload',
  },
  managedEnums: {
    createManagedEnum: '/managed-enums',
    deleteManagedEnum: (id: string) => `/managed-enums/${id}`,
    getManagedEnums: '/managed-enums',
    updateManagedEnum: (id: string) => `/managed-enums/${id}`,
  },
  jobOperations: {
    getJobOperations: '/job-operations',
    pollJobOperations: '/job-operations/poll',
    getJobOperationDetail: (id: string) => `/job-operations/detail/${id}`,
    getJobOperationFilterValues: '/job-operations/filter-values',
  },
  organization: {
    getOrganizationUsers: '/organization/users',
    inviteOrganizationUser: '/organization/users',
    removeOrganizationUser: (userId: string) => `/organization/users/${userId}`,
    revokeOrganizationInvitation: (invitationId: string) => `/organization/invitations/${invitationId}`,
  },
  meteringCoverage: {
    downloadCoverage: '/metering-data/coverage/download',
    downloadGaps: '/metering-data/coverage/gaps/download',
    getCoverage: '/metering-data/coverage',
    getGaps: (meteringPointId: string) => `/metering-data/coverage/${meteringPointId}/gaps`,
    getGranularities: '/metering-data/coverage/granularities',
  },
  meteringPointStates: {
    getMeteringPointStates: '/metering-point-states',
  },
  meteringPoints: {
    createMeteringPoint: '/metering-points',
    getMeteringPoints: '/metering-points',
    updateMeteringPoint: (id: string) => `/metering-points/${id}`,
  },
  metrics: {
    getAllocationsPivotTable: '/metrics/allocations/pivot-table',
    getConsumerOverview: '/metrics/consumer/overview',
    getECDashboardAssociatedFilterValues: '/metrics/end-consumer/associated-filter-values',
    getPerimeter: '/metrics/perimeter',

    // Demand
    getDemand: '/metrics/demand',
    getDemandAssociatedFilterValues: '/metrics/demand/associated-filter-values',
    getDemandCarbon: '/metrics/demand/carbon',
    getDemandFilterValues: '/metrics/demand/filter-values',
    getDemandLatest: '/metrics/demand/latest',
    getDemandMatchingResolutions: '/metrics/demand/matching-resolutions',
    getDemandPivotTable: '/metrics/demand/pivot-table',
    getDemandPlottingResolutions: '/metrics/demand/plotting-resolutions',

    // Portfolio
    getPortfolioMatchingResolutions: '/metrics/portfolio/matching-resolutions',
    getPortfolioLatest: '/metrics/portfolio/latest',
    getPortfolio: '/metrics/portfolio',
    getPortfolioFilterValues: '/metrics/portfolio/filter-values',
    getPortfolioPivotTable: '/metrics/portfolio/pivot-table',
    getPortfolioPlottingResolutions: '/metrics/portfolio/plotting-resolutions',

    // Supply
    getSupplyFilterValues: '/metrics/supply/filter-values',
    getSupplyPlottingResolutions: '/metrics/supply/plotting-resolutions',
    getSupply: '/metrics/supply',
    getSupplyAssociatedFilterValues: '/metrics/supply/associated-filter-values',
    getSupplyLatest: '/metrics/supply/latest',
    getSupplyPivotTable: '/metrics/supply/pivot-table',
  },
  portfolio: {
    getDemandPortfolio: '/portfolio/demand/download',
    getDemandPortfolioStatus: (id: string) => `/portfolio/demand/upload/${id}`,
    getSupplyPortfolio: '/portfolio/production-device-and-metering-point/download',
    getSupplyPortfolioStatus: (id: string) => `/portfolio/production-device-and-metering-point/upload/${id}`,
    postDemandUpload: '/portfolio/demand/upload',
    postSupplyUpload: '/portfolio/production-device-and-metering-point/upload',
  },
  ppas: {
    createPpa: '/ppas',
    deletePpa: (id: string) => `/ppas/${id}`,
    getPpa: (id: string) => `/ppas/${id}`,
    getPpas: '/ppas',
  },
  productionDevices: {
    createProductionDevice: '/assets',
    deleteProductionDevice: (id: string) => `/assets/${id}`,
    getProductionDevice: (id: string) => `/assets/${id}`,
    getProductionDevicesWithMeteringPoints: '/production-devices-with-metering-points',
    updateProductionDevice: (id: string) => `/assets/${id}`,
  },
  registries: {
    createRegistryAccountHolder: '/registries/account-holders',
    getRegistries: '/registries',
    getRegistryAccountHolders: '/registries/account-holders',
    getRegistryAccountHoldersWithAccounts: '/registries/account-holders-with-accounts',
    getRegistryActionMetadataSchema: (registry: string, actionType: string) => `/registries/${registry}/registry-actions/${actionType}/metadata-schema`,
    updateRegistryAccountHolder: (id: string) => `/registries/account-holders/${id}`,
  },
  registryTransactions: {
    getRegistryTransaction: (id: string) => `/registry-transactions/${id}`,
    getRegistryTransactions: '/registry-transactions',
    getUnreconciledOutTransactions: '/registry-transactions/unreconciled-out-transactions',
    getRegistryTransactionVolumes: (id: string) => `/registry-transactions/${id}/volumes`,
    getVolumesForTransactions: '/registry-transactions/volumes-for-transactions',
    getRegistryTransactionVolumesBundles: (transactionId: string, volumeId: string) => `/registry-transactions/${transactionId}/volumes/${volumeId}/bundles`,
    recognition: {
      recogniseInboundTransactionsWithoutReconciliation: '/registry-transactions/recognition/inbound/recognise-transactions-without-reconciliation',
      getCandidateBatchesForReconciliation: (id: string) => `/registry-transactions/${id}/candidate-batches-for-reconciliation`,
      getCandidateDealTranchesForTransactions: '/registry-transactions/candidate-deals-for-transactions',
      getCandidateMeteredSupplyForTransactions: '/registry-transactions/candidate-metered-supply-for-transactions',
      recogniseInternalTransfer: (id: string) => `/registry-transactions/${id}/recognition/recognise-internal-transfer`,
      // Deal tranches, metered supply and no reconciliation in one operation; not the contracted supply route below.
      recogniseInboundTransactions: '/registry-transactions/recognition/inbound/recognise-inbound-transactions-with-contracted-supply-reconciliation',
      recogniseInboundTransactionsWithContractedSupplyReconciliation: '/registry-transactions/recognition/inbound/recognise-transactions-with-contracted-supply-reconciliation',
      recogniseInboundTransactionWithSpotDealReconciliation: (id: string) => `/registry-transactions/${id}/recognition/inbound/recognise-transaction-with-spot-deal-reconciliation`,
      recogniseOutboundTransactionCertificateVolumesWithDealReconciliation: '/registry-transactions/recognition/outbound/recognise-transaction-certificate-volumes-with-deal-reconciliation',
      recogniseOutboundTransactionWithAllocationBatchReconciliation: '/registry-transactions/recognition/outbound/recognise-transaction-with-allocation-batch-reconciliation',
      recogniseOutboundTransactionsWithContractedDemandReconciliation: '/registry-transactions/recognition/outbound/recognise-transactions-with-contracted-demand-reconciliation',
      recogniseOutboundTransactionsWithoutReconciliation: '/registry-transactions/recognition/outbound/recognise-transactions-without-reconciliation',
    },
  },
  reports: {
    deleteReport: (id: string) => `/reports/${id}`,
    downloadReport: (id: string) => `/reports/${id}/download`,
    downloadReports: '/reports/download',
    generateBatchReport: '/reports/batch',
    getFilterLevel: '/reports/filter-values/level',
    getFilterName: '/reports/filter-values/name',
    getReportTemplates: '/reports/templates',
    getReports: '/reports',
  },
  tags: {
    getDemandTagValues: (key: string) => `/tags/demand/${encodeURIComponent(key)}/values`,
    getFilterableDemandTags: '/tags/demand/filterable',
    getFilterableSupplyTags: '/tags/supply/filterable',
    getSupplyTagValues: (key: string) => `/tags/supply/${encodeURIComponent(key)}/values`,
    getTags: '/tags',
  },
  termsAndConditions: {
    acceptTermsAndConditions: (id: string, userEmail: string) => `/terms-and-conditions/${id}/users/${userEmail}/accept`,
    downloadTermsAndConditions: (id: string) => `/terms-and-conditions/${id}/download`,
    getTermsAndConditions: '/terms-and-conditions',
    getTermsAndConditionsUserAcceptance: (userEmail: string) => `/terms-and-conditions/users/${userEmail}/check`,
  },
  upload: {
    example: {
      deals: '/deals/upload/example', // TODO: update ?
      inventoryBundle: '/certificate-bundle/holding/upload/example', // TODO: update
      inventoryVolume: '/certificate-bundle/balances/upload/example', // TODO: update ?
      lossFactorCodes: '/esna/loss-factor-codes/upload/example',
      registryTransactions: '/registry-transactions/bundles/upload/example', // TODO: update
      transactionsBundle: '/registry-transactions/bundles/upload/example',
      transactionsVolume: '/registry-transactions/volumes/upload/example',
    },
    schema: {
      consumptionMeasurements: '/multipoint-consumption-files/upload/schema',
      deals: '/deals/files/upload/schema', // TODO: update
      lesContracts: '/esna/contracts/files/upload/schema',
      lossFactorCodes: '/esna/loss-factor-codes/upload/schema',
      demandPortfolio: '/portfolio/demand/upload/schema',
      inventoryBundle: '/certificate-bundle/holding/upload/schema', // TODO: update
      inventoryVolume: '/certificate-bundle/balances/upload/schema',
      productionMeasurements: '/multipoint-production-files/upload/schema',
      registryTransactions: '/registry-transactions/bundles/upload/schema',
      supplyPortfolio: '/portfolio/production-device-and-metering-point/upload/schema',
      transactionsBundle: '/registry-transactions/bundles/upload/schema',
      transactionsVolume: '/registry-transactions/volumes/upload/schema',
    },
  },
  userConfig: {
    getUserConfig: '/user-config',
  },
} as const;

export type Route = (typeof routes)[keyof typeof routes];
